import { cn, type DataTableColumn } from "@etudescarre/ui";
import { isNotNil } from "@etudescarre/utils";
import { FilePdf as FilePdfIcon } from "@phosphor-icons/react";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router";
import { apiClient } from "@/lib/api/client";
import { formatDay } from "@/lib/utils/date";
import { Classeur, type FilterDef } from "../components/classeur";
import type { FilterValues } from "../components/classeur/types";
import {
  useClasseurFilters,
  useClasseurState,
} from "../components/classeur/use-classeur-data";

// Écran 039 — Classeur visas entreprise générale reçus : visas reçus
// par le contrat de l'utilisateur (rang 2), affichés par document visé, avec
// les observations formulées et l'accès aux fichiers (visa + document).

type VisaReceived = {
  id: number;
  numero: string | null;
  dateEnvoi: string | null;
  authorEmployee: string | null;
  fichierId: number | null;
  documents: Array<{
    documentVersionId: number;
    number: string;
    name: string;
    index: string | null;
    lots: string[];
    zones: string[];
    fichierPdfId: number | null;
    observations: string[];
  }>;
};

export type VisaRecuRow = {
  id: string;
  numero: string | null;
  dateEnvoi: string | null;
  authorEmployee: string | null;
  visaFichierId: number | null;
  docNumber: string;
  docName: string;
  docIndex: string | null;
  lots: string[];
  zones: string[];
  docFichierPdfId: number | null;
  observations: string[];
};

/** Une ligne par couple (visa, document visé). Les visas sont déjà triés par n° côté API. */
export function flattenVisas(visas: VisaReceived[]): VisaRecuRow[] {
  return visas.flatMap((visa) =>
    visa.documents.map((doc) => ({
      id: `${visa.id}-${doc.documentVersionId}`,
      numero: visa.numero,
      dateEnvoi: visa.dateEnvoi,
      authorEmployee: visa.authorEmployee,
      visaFichierId: visa.fichierId,
      docNumber: doc.number,
      docName: doc.name,
      docIndex: doc.index,
      lots: doc.lots,
      zones: doc.zones,
      docFichierPdfId: doc.fichierPdfId,
      observations: doc.observations,
    })),
  );
}

/** Filtrage client par lot et zone/bâtiment. */
export function filterRows(
  rows: VisaRecuRow[],
  activeFilters: FilterValues,
): VisaRecuRow[] {
  const lots = activeFilters.lot ?? [];
  const zones = activeFilters.zone ?? [];
  return rows.filter(
    (row) =>
      (lots.length === 0 || row.lots.some((l) => lots.includes(l))) &&
      (zones.length === 0 || row.zones.some((z) => zones.includes(z))),
  );
}

const truncateClass = "block max-w-full truncate";

export function ClasseurVisasRecus() {
  const { yardId } = useParams<{ yardId: string }>();
  const [state, setState] = useClasseurState<VisaRecuRow>();

  useEffect(() => {
    async function fetchData() {
      setState((prev) => ({ ...prev, isLoading: true }));
      const { data } = await apiClient.GET("/api/yards/{yardId}/visas", {
        params: {
          path: { yardId: yardId ?? "" },
          query: { direction: "received" },
        },
      });
      if (isNotNil(data)) {
        const rows = flattenVisas(data.data.items);
        setState({ items: rows, total: data.data.total, isLoading: false });
      } else {
        setState({ items: [], total: 0, isLoading: false });
      }
    }
    fetchData();
  }, [yardId, setState]);

  const filters: FilterDef[] = useMemo(() => {
    const lots = [...new Set(state.items.flatMap((r) => r.lots))].sort();
    const zones = [...new Set(state.items.flatMap((r) => r.zones))].sort();
    return [
      {
        key: "lot",
        label: "Lot",
        options: lots.map((l) => ({ label: l, value: l })),
      },
      {
        key: "zone",
        label: "Zone / Bâtiment",
        options: zones.map((z) => ({ label: z, value: z })),
      },
    ];
  }, [state.items]);

  const { activeFilters, onFilterChange } = useClasseurFilters({ filters });

  const visibleRows = useMemo(
    () => filterRows(state.items, activeFilters),
    [state.items, activeFilters],
  );

  async function openFichier(fichierId: number) {
    const { data } = await apiClient.GET(
      "/api/yards/{yardId}/fichiers/{fichierId}/url",
      {
        params: {
          path: { yardId: yardId ?? "", fichierId: String(fichierId) },
        },
      },
    );
    if (isNotNil(data)) {
      window.open(data.data.inlineUrl, "_blank");
    }
  }

  const columns: DataTableColumn<VisaRecuRow>[] = [
    { id: "numero", header: "N°", accessor: "numero" },
    {
      id: "dateEnvoi",
      header: "Date envoi",
      accessor: "dateEnvoi",
      cell: ({ value }) => (value ? formatDay(value as string) : "-"),
    },
    { id: "authorEmployee", header: "Auteur", accessor: "authorEmployee" },
    {
      id: "visaPdf",
      header: "Visa",
      accessor: "visaFichierId",
      cell: ({ row }) =>
        isNotNil(row.visaFichierId) ? (
          <button
            type="button"
            title="Ouvrir le fichier du visa"
            onClick={() => {
              if (isNotNil(row.visaFichierId)) openFichier(row.visaFichierId);
            }}
          >
            <FilePdfIcon size={18} className="text-red-600" />
          </button>
        ) : (
          <span className="text-zinc-300">-</span>
        ),
    },
    { id: "docNumber", header: "N° document", accessor: "docNumber" },
    {
      id: "docName",
      header: "Intitulé",
      accessor: "docName",
      cell: ({ value }) => (
        <span className={truncateClass} title={String(value)}>
          {value}
        </span>
      ),
    },
    { id: "docIndex", header: "Indice", accessor: "docIndex" },
    {
      id: "lots",
      header: "Lot",
      accessor: (row) => row.lots.join(", "),
      cell: ({ row }) => (
        <span className={truncateClass} title={row.lots.join(", ")}>
          {row.lots.join(", ")}
        </span>
      ),
    },
    {
      id: "observations",
      header: "Observations",
      accessor: (row) => row.observations.join(" ; "),
      cellClassName: "text-left",
      cell: ({ row }) => (
        <span
          className={cn(truncateClass, "text-left")}
          title={row.observations.join("\n")}
        >
          {row.observations.join(" ; ")}
        </span>
      ),
    },
    {
      id: "docPdf",
      header: "PDF",
      accessor: "docFichierPdfId",
      cell: ({ row }) =>
        isNotNil(row.docFichierPdfId) ? (
          <button
            type="button"
            title="Ouvrir le PDF du document"
            onClick={() => {
              if (isNotNil(row.docFichierPdfId)) {
                openFichier(row.docFichierPdfId);
              }
            }}
          >
            <FilePdfIcon size={18} className="text-red-600" />
          </button>
        ) : (
          <span className="text-zinc-300">-</span>
        ),
    },
  ];

  return (
    <Classeur
      title="Classeur visas entreprise générale"
      items={visibleRows}
      columns={columns}
      filters={filters}
      activeFilters={activeFilters}
      onFilterChange={onFilterChange}
      isLoading={state.isLoading}
      emptyMessage="Aucun visa reçu"
      total={visibleRows.length}
    />
  );
}
